// @ts-check
import {
  Check,
  Copy,
  Flag,
  Mail,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  UserPlus,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingScreen from '../components/LoadingScreen';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import Modal from '../components/ui/Modal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import { reportBulkResult, runBulk } from '../services/bulkService';
import api from '../utils/apiClient';

const STATUS_BADGES = {
  pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
  approved: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  rejected: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  flagged: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
  deleted: 'bg-accent text-foreground/80',
};

const TABS = ['all', 'pending', 'approved', 'rejected', 'flagged', 'deleted'];

const SOURCE_FILTERS = [
  { value: '', label: 'All Sources' },
  { value: 'customer_purchase', label: 'Customer Purchase' },
  { value: 'invited', label: 'Invited' },
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'rating_asc', label: 'Lowest rating first' },
  { value: 'rating_desc', label: 'Highest rating first' },
  { value: 'helpful_desc', label: 'Most helpful first' },
];

const INVITE_LIST_FILTERS = [
  { value: 'outstanding', label: 'Outstanding (no action taken)' },
  { value: 'used', label: 'Used' },
  { value: 'all', label: 'All' },
];

const renderStars = (rating) => {
  return Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={i < rating ? 'text-yellow-500' : 'text-muted-foreground/50'}>
      {i < rating ? '★' : '☆'}
    </span>
  ));
};

const getInviteStatus = (invite) => {
  if (invite.used_at)
    return { label: 'Used', className: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' };
  if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
    return { label: 'Expired', className: 'bg-accent text-muted-foreground' };
  }
  return {
    label: 'Outstanding',
    className: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
  };
};

/**
 * Row actions "..." menu, rendered via a portal to document.body so it's never
 * clipped by the table's overflow-x-auto wrapper — previously it rendered inline
 * inside the scrollable table cell and got hidden whenever the table was short.
 */
const RowActionsMenu = ({ review, adminRole, onStatusChange, onReachOut, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 176; // w-44
      setCoords({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const isDeleted = review.status === 'deleted';

  return (
    <>
      <button ref={btnRef} onClick={toggle} className="p-1 hover:bg-accent rounded-lg">
        <MoreHorizontal size={18} />
      </button>
      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div
              className="fixed bg-card border rounded-lg shadow-lg z-50 w-44 py-1"
              style={{ top: coords.top, left: coords.left }}
            >
              {isDeleted ? (
                <button
                  onClick={() => {
                    setOpen(false);
                    onStatusChange(review.id, 'pending');
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50"
                >
                  <RotateCcw size={14} className="text-blue-600 dark:text-blue-400" /> Restore
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onStatusChange(review.id, 'approved');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50"
                  >
                    <Check size={14} className="text-green-600 dark:text-green-400" /> Approve
                  </button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onStatusChange(review.id, 'rejected');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50"
                  >
                    <XCircle size={14} className="text-red-600 dark:text-red-400" /> Reject
                  </button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onStatusChange(review.id, 'flagged');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50"
                  >
                    <Flag size={14} className="text-orange-600 dark:text-orange-400" /> Flag
                  </button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onReachOut(review);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground/80 hover:bg-muted/50"
                  >
                    <Mail size={14} className="text-blue-600 dark:text-blue-400" /> Reach Out
                  </button>
                  {adminRole === 'super_admin' && (
                    <button
                      onClick={() => {
                        setOpen(false);
                        onDelete(review.id);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  )}
                </>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

const ReviewsPage = () => {
  const toast = useToast();
  const [view, setView] = useState('reviews'); // 'reviews' | 'invites'

  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0, flagged: 0, deleted: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [previewReview, setPreviewReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  // Touched-since-open flag for the reply textarea, reset by handleSaveReply
  // and whenever a different review's reply text is loaded in.
  const [isReplyDirty, setIsReplyDirty] = useState(false);
  const [savingReply, setSavingReply] = useState(false);
  const [aiOpinion, setAiOpinion] = useState(null);
  const [gettingAiOpinion, setGettingAiOpinion] = useState(false);
  const [reachOutModal, setReachOutModal] = useState(null);
  const [reachOutSubject, setReachOutSubject] = useState('');
  const [reachOutMessage, setReachOutMessage] = useState('');
  // Touched-since-open flag, reset by handleReachOut's success and by both
  // explicit close paths (X / Cancel) once confirmed.
  const [isReachOutDirty, setIsReachOutDirty] = useState(false);
  const [generatingReachOut, setGeneratingReachOut] = useState(false);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteProduct, setInviteProduct] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  // Touched-since-open flag — cleared once a link has actually been generated
  // (nothing left to lose by closing at that point) as well as by
  // closeInviteModal's own reset.
  const [isInviteDirty, setIsInviteDirty] = useState(false);
  const searchDebounce = useRef(null);

  const [inviteListFilter, setInviteListFilter] = useState('outstanding');
  const [inviteList, setInviteList] = useState([]);
  const [inviteListLoading, setInviteListLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const adminRole = adminUser.role;
  const selection = useRowSelection(
    reviews.map((r) => r.id),
    { resetKey: `${filter}|${sourceFilter}` },
  );

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (sourceFilter) params.set('review_source', sourceFilter);
      if (sortBy) params.set('sort', sortBy);
      const qs = params.toString();
      const data = await api.get(`/api/admin/reviews${qs ? `?${qs}` : ''}`);
      setReviews(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filter, sourceFilter, sortBy, toast]);

  const loadCounts = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/reviews/count');
      setCounts(data);
    } catch {
      // Counts are non-critical
    }
  }, []);

  useEffect(() => {
    if (view === 'reviews') {
      loadReviews();
      loadCounts();
    }
  }, [view, loadReviews, loadCounts]);

  const loadInviteList = useCallback(async () => {
    setInviteListLoading(true);
    try {
      const params = new URLSearchParams();
      if (inviteListFilter === 'outstanding') params.set('used', 'false');
      if (inviteListFilter === 'used') params.set('used', 'true');
      const qs = params.toString();
      const data = await api.get(`/api/admin/reviews/invites${qs ? `?${qs}` : ''}`);
      setInviteList(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || 'Failed to load invites');
    } finally {
      setInviteListLoading(false);
    }
  }, [inviteListFilter, toast]);

  useEffect(() => {
    if (view === 'invites') loadInviteList();
  }, [view, loadInviteList]);

  const handleStatusChange = async (reviewId, newStatus) => {
    try {
      await api.patch(`/api/admin/reviews/${reviewId}`, { status: newStatus });
      toast.success(newStatus === 'pending' ? 'Review restored' : `Review ${newStatus} successfully`);
      loadReviews();
      loadCounts();
    } catch (error) {
      toast.error(error.message || 'Failed to update review');
    }
  };

  const handleBulkAction = async (action, verb) => {
    if (selection.count === 0) return;
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/reviews/bulk', { ids: selection.selectedIds, action });
      reportBulkResult(toast, result, verb, 'review');
      selection.clear();
      setConfirmBulkDelete(false);
      loadReviews();
      loadCounts();
    } catch (error) {
      toast.error(error.message || 'Failed to perform bulk action');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleReachOut = async () => {
    if (!reachOutModal) return;
    try {
      await api.post(`/api/admin/reviews/${reachOutModal.id}/reach-out`, {
        subject: reachOutSubject,
        message: reachOutMessage,
      });
      toast.success('Email sent successfully');
      setIsReachOutDirty(false);
      setReachOutModal(null);
      setReachOutSubject('');
      setReachOutMessage('');
    } catch (error) {
      toast.error(error.message || 'Failed to send email');
    }
  };

  // X button and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose. handleReachOut's success path bypasses this.
  const handleReachOutClose = () => {
    if (isReachOutDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setIsReachOutDirty(false);
    setReachOutModal(null);
    setReachOutSubject('');
    setReachOutMessage('');
  };

  const generateReachOut = async () => {
    if (!reachOutModal) return;
    if ((reachOutSubject || reachOutMessage) && !window.confirm('This will replace the current draft. Continue?'))
      return;

    setGeneratingReachOut(true);
    try {
      const result = await api.post(`/api/admin/reviews/${reachOutModal.id}/generate-reach-out`);
      setReachOutSubject(result.subject || '');
      setReachOutMessage(result.message || '');
      setIsReachOutDirty(true);
    } catch (error) {
      toast.error(error.message || 'Failed to generate draft');
    } finally {
      setGeneratingReachOut(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (
      !window.confirm(
        'Delete this review? It moves to the Deleted tab (kept for training reference) and disappears from the site.',
      )
    )
      return;
    try {
      await api.del(`/api/admin/reviews/${reviewId}`);
      toast.success('Review deleted');
      loadReviews();
      loadCounts();
    } catch (error) {
      toast.error(error.message || 'Failed to delete review');
    }
  };

  const handleSaveReply = async () => {
    if (!previewReview) return;
    setSavingReply(true);
    try {
      await api.patch(`/api/admin/reviews/${previewReview.id}`, { admin_reply: replyText });
      toast.success('Reply saved');
      setPreviewReview((prev) => (prev ? { ...prev, admin_reply: replyText } : prev));
      setIsReplyDirty(false);
      loadReviews();
    } catch (error) {
      toast.error(error.message || 'Failed to save reply');
    } finally {
      setSavingReply(false);
    }
  };

  const handleGetAiOpinion = async () => {
    if (!previewReview) return;
    setGettingAiOpinion(true);
    try {
      const result = await api.post(`/api/admin/reviews/${previewReview.id}/ai-opinion`);
      setAiOpinion(result);
    } catch (error) {
      toast.error(error.message || 'Failed to get AI opinion');
    } finally {
      setGettingAiOpinion(false);
    }
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteQuery('');
    setInviteResults([]);
    setInviteProduct(null);
    setInviteEmail('');
    setInviteName('');
    setInviteLink('');
    setInviteEmailSent(false);
    setIsInviteDirty(false);
  };

  // The X button is this modal's only dismiss action — confirm first if
  // there's anything typed that hasn't turned into a generated invite yet.
  const handleInviteCloseClick = () => {
    if (isInviteDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    closeInviteModal();
  };

  const loadDefaultInviteProducts = async () => {
    try {
      const data = await api.get('/api/admin/products/?limit=15');
      setInviteResults(data.items || []);
    } catch {
      setInviteResults([]);
    }
  };

  const handleInviteQueryChange = (value) => {
    setInviteQuery(value);
    setInviteProduct(null);
    setIsInviteDirty(true);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!value.trim()) {
      loadDefaultInviteProducts();
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      try {
        const data = await api.get(`/api/admin/products/?search=${encodeURIComponent(value)}&limit=15`);
        setInviteResults(data.items || []);
      } catch {
        setInviteResults([]);
      }
    }, 300);
  };

  const handleCreateInvite = async () => {
    if (!inviteProduct) {
      toast.error('Select a product');
      return;
    }
    const email = inviteEmail.trim();
    setInviteSubmitting(true);
    try {
      const result = await api.post('/api/admin/reviews/invite', {
        product_id: inviteProduct.id,
        email: email || null,
        name: inviteName.trim() || null,
      });
      setInviteLink(result.invite_url);
      setInviteEmailSent(Boolean(result.email_sent));
      setIsInviteDirty(false);
      if (email && result.email_sent) {
        toast.success(`Invite emailed to ${email}`);
      } else if (email) {
        toast.error('Could not send the email automatically — copy the link below instead');
      } else {
        toast.success('Invite link generated');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  const handleResendInvite = async (inviteId) => {
    setResendingId(inviteId);
    try {
      const result = await api.post(`/api/admin/reviews/invite/${inviteId}/resend`);
      toast.success(result.message || 'Invite resent');
    } catch (error) {
      toast.error(error.message || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  if (view === 'reviews' && loading && reviews.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">Reviews</h1>
          <p className="text-muted-foreground">Manage customer reviews and ratings</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setInviteModalOpen(true);
              loadDefaultInviteProducts();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus size={18} />
            Invite Reviewer
          </button>
          <button
            onClick={() => {
              if (view === 'reviews') {
                loadReviews();
                loadCounts();
              } else {
                loadInviteList();
              }
            }}
            disabled={loading || inviteListLoading}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading || inviteListLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Top-level view switcher */}
      <div className="flex gap-6 mb-6 border-b overflow-x-auto">
        {[
          { key: 'reviews', label: 'Reviews' },
          { key: 'invites', label: 'Invite Links' },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex-shrink-0 whitespace-nowrap px-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
              view === v.key
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'reviews' && (
        <>
          {/* Status Filter Tabs + Source Filter */}
          <div className="flex flex-wrap justify-between items-center gap-2 mb-6 border-b">
            <div className="flex gap-2 overflow-x-auto max-w-full">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setFilter(tab);
                  }}
                  className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                    filter === tab
                      ? 'border-foreground text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab} ({counts[tab] || 0})
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="text-sm border border-input rounded-lg px-2 py-1.5"
              >
                {SOURCE_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-input rounded-lg px-2 py-1.5"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filter === 'deleted' && (
            <p className="text-xs text-muted-foreground mb-4 -mt-2">
              Kept as a reference for training moderators and the AI opinion assist on what kind of content gets
              rejected. Hidden from the site.
            </p>
          )}

          <BulkActionBar count={selection.count} onClear={selection.clear}>
            <BulkActionBar.Button onClick={() => handleBulkAction('approve', 'approved')} disabled={bulkBusy}>
              Approve
            </BulkActionBar.Button>
            <BulkActionBar.Button onClick={() => handleBulkAction('reject', 'rejected')} disabled={bulkBusy}>
              Reject
            </BulkActionBar.Button>
            <BulkActionBar.Button onClick={() => handleBulkAction('flag', 'flagged')} disabled={bulkBusy}>
              Flag
            </BulkActionBar.Button>
            {adminRole === 'super_admin' && filter !== 'deleted' && (
              <BulkActionBar.Button tone="danger" onClick={() => setConfirmBulkDelete(true)} disabled={bulkBusy}>
                Delete
              </BulkActionBar.Button>
            )}
          </BulkActionBar>

          {/* Table */}
          <div className="bg-card rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <HeaderCheckbox selection={selection} />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Review</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                        Loading reviews...
                      </td>
                    </tr>
                  ) : reviews.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                        No reviews found
                      </td>
                    </tr>
                  ) : (
                    reviews.map((review) => (
                      <tr key={review.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <RowCheckbox selection={selection} id={review.id} />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground max-w-[150px] truncate">
                          {review.product_name || 'Unknown Product'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-foreground">
                            {review.customer_name || 'Anonymous'}
                          </div>
                          <div className="text-xs text-muted-foreground">{review.customer_email}</div>
                          {review.verified_purchase ? (
                            <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300">
                              Verified
                            </span>
                          ) : review.review_source === 'invited' ? (
                            <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300">
                              Invited
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{renderStars(review.rating)}</td>
                        <td className="px-4 py-3 text-sm text-foreground/80 max-w-[250px]">
                          <button
                            onClick={() => {
                              setPreviewReview(review);
                              setReplyText(review.admin_reply || '');
                              setIsReplyDirty(false);
                              setAiOpinion(
                                review.ai_recommendation
                                  ? {
                                      recommendation: review.ai_recommendation,
                                      reasoning: review.ai_reasoning,
                                      concerns: review.ai_concerns || [],
                                      opinion_at: review.ai_opinion_at,
                                    }
                                  : null,
                              );
                            }}
                            className="text-left hover:text-blue-600 dark:hover:text-blue-400 truncate block max-w-full"
                          >
                            {review.body?.length > 80 ? review.body.substring(0, 80) + '...' : review.body || 'No text'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${STATUS_BADGES[review.status] || 'bg-muted text-foreground'}`}
                          >
                            {review.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {new Date(review.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 relative">
                          <RowActionsMenu
                            review={review}
                            adminRole={adminRole}
                            onStatusChange={handleStatusChange}
                            onReachOut={setReachOutModal}
                            onDelete={handleDelete}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {view === 'invites' && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <select
              value={inviteListFilter}
              onChange={(e) => setInviteListFilter(e.target.value)}
              className="text-sm border border-input rounded-lg px-2 py-1.5"
            >
              {INVITE_LIST_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-card rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Reviewer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {inviteListLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                        Loading invites...
                      </td>
                    </tr>
                  ) : inviteList.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                        No invites found
                      </td>
                    </tr>
                  ) : (
                    inviteList.map((invite) => {
                      const status = getInviteStatus(invite);
                      const canResend = !invite.used_at && invite.email && status.label !== 'Expired';
                      return (
                        <tr key={invite.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm font-medium text-foreground max-w-[150px] truncate">
                            {invite.product_name || 'Unknown Product'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-foreground">{invite.name || 'No name'}</div>
                            <div className="text-xs text-muted-foreground">{invite.email || 'No email on file'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.className}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {new Date(invite.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => copyLink(invite.invite_url)}
                                className="p-1.5 border rounded-lg hover:bg-muted/50"
                                title="Copy link"
                              >
                                <Copy size={14} />
                              </button>
                              {canResend && (
                                <button
                                  onClick={() => handleResendInvite(invite.id)}
                                  disabled={resendingId === invite.id}
                                  className="p-1.5 border rounded-lg hover:bg-muted/50 disabled:opacity-50"
                                  title="Resend email"
                                >
                                  <Send size={14} className={resendingId === invite.id ? 'animate-pulse' : ''} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Review Preview Modal */}
      {previewReview && (
        <Modal maxWidth="lg" closeOnBackdrop={false} className="rounded-lg shadow-xl" hasUnsavedChanges={isReplyDirty}>
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold">Review Details</h2>
            <button
              onClick={() => {
                if (isReplyDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
                setPreviewReview(null);
                setAiOpinion(null);
              }}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto">
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Product</span>
              <p className="font-medium">{previewReview.product_name || 'Unknown Product'}</p>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Customer</span>
              <p className="font-medium">{previewReview.customer_name || 'Anonymous'}</p>
              <p className="text-sm text-muted-foreground">{previewReview.customer_email}</p>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Rating</span>
              <div className="text-lg">{renderStars(previewReview.rating)}</div>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Review</span>
              <p className="mt-1 text-foreground whitespace-pre-wrap">{previewReview.body || 'No text'}</p>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={handleGetAiOpinion}
                disabled={gettingAiOpinion}
                className="flex items-center gap-2 px-3 py-1.5 text-xs border rounded-lg hover:bg-muted/50 disabled:opacity-50"
                title="Get an AI opinion on this review's content — advisory only, you decide"
              >
                <Sparkles size={14} className={gettingAiOpinion ? 'animate-pulse' : ''} />
                {gettingAiOpinion ? 'Thinking...' : aiOpinion ? 'Re-check with AI' : 'Get AI Opinion'}
              </button>
              {aiOpinion && (
                <div
                  className={`mt-3 p-3 rounded-lg border text-sm ${
                    aiOpinion.recommendation === 'approve'
                      ? 'border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/50'
                      : aiOpinion.recommendation === 'reject'
                        ? 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/50'
                        : 'border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                        aiOpinion.recommendation === 'approve'
                          ? 'bg-green-600 text-white'
                          : aiOpinion.recommendation === 'reject'
                            ? 'bg-red-600 text-white'
                            : 'bg-orange-500 text-white'
                      }`}
                    >
                      AI suggests: {aiOpinion.recommendation}
                    </span>
                  </div>
                  <p className="text-foreground/80">{aiOpinion.reasoning}</p>
                  {aiOpinion.concerns?.length > 0 && (
                    <ul className="list-disc list-inside mt-1 text-muted-foreground">
                      {aiOpinion.concerns.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-muted-foreground/70 mt-2">
                    Advisory only — you make the final call.
                    {aiOpinion.opinion_at && ` Checked ${new Date(aiOpinion.opinion_at).toLocaleString()}.`}
                  </p>
                </div>
              )}
            </div>

            {previewReview.media && previewReview.media.length > 0 && (
              <div className="mb-4">
                <span className="text-sm text-muted-foreground">Media</span>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {previewReview.media.map((m, i) =>
                    m.media_type === 'video' ? (
                      <video key={i} src={m.media_url} controls className="w-24 h-24 object-cover rounded-lg border" />
                    ) : (
                      <img
                        key={i}
                        src={m.media_url}
                        alt={`Review media ${i + 1}`}
                        className="w-24 h-24 object-cover rounded-lg border"
                      />
                    ),
                  )}
                </div>
              </div>
            )}
            <div className="mb-4 flex items-center gap-3">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${STATUS_BADGES[previewReview.status] || 'bg-muted text-foreground'}`}
              >
                {previewReview.status}
              </span>
              {previewReview.verified_purchase ? (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-50 dark:bg-green-950/50 text-green-700 dark:text-green-300">
                  Verified Purchase
                </span>
              ) : previewReview.review_source === 'invited' ? (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300">
                  Invited
                </span>
              ) : null}
              <span className="text-sm text-muted-foreground">
                {new Date(previewReview.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="mb-4">
              <label className="block text-sm text-muted-foreground mb-1">Public Reply</label>
              <textarea
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  setIsReplyDirty(true);
                }}
                placeholder="Reply visible to shoppers under this review on the product page..."
                rows={3}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={handleSaveReply}
                disabled={savingReply}
                className="mt-2 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {savingReply ? 'Saving...' : 'Save Reply'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reach Out Modal */}
      {reachOutModal && (
        <Modal
          maxWidth="md"
          closeOnBackdrop={false}
          className="rounded-lg shadow-xl"
          hasUnsavedChanges={isReachOutDirty}
        >
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold">Reach Out to Customer</h2>
            <button onClick={handleReachOutClose} className="text-muted-foreground/70 hover:text-foreground">
              <X size={20} />
            </button>
          </div>
          <div className="p-6">
            <p className="text-sm text-muted-foreground mb-4">
              Sending to: <span className="font-medium text-foreground">{reachOutModal.customer_email}</span>
            </p>
            <button
              type="button"
              onClick={generateReachOut}
              disabled={generatingReachOut}
              className="flex items-center gap-2 px-3 py-1.5 mb-4 text-xs border rounded-lg hover:bg-muted/50 disabled:opacity-50"
              title="Draft a subject & message from this review, tone-adjusted by rating"
            >
              <Sparkles size={14} className={generatingReachOut ? 'animate-pulse' : ''} />
              {generatingReachOut ? 'Drafting...' : 'Draft with AI'}
            </button>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Subject</label>
              <input
                type="text"
                value={reachOutSubject}
                onChange={(e) => {
                  setReachOutSubject(e.target.value);
                  setIsReachOutDirty(true);
                }}
                placeholder="Regarding your review..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Message</label>
              <textarea
                value={reachOutMessage}
                onChange={(e) => {
                  setReachOutMessage(e.target.value);
                  setIsReachOutDirty(true);
                }}
                placeholder="Write your message..."
                rows={5}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={handleReachOutClose} className="px-4 py-2 border rounded-lg hover:bg-muted/50">
                Cancel
              </button>
              <button
                onClick={handleReachOut}
                disabled={!reachOutSubject || !reachOutMessage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Send Email
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Invite Reviewer Modal */}
      {inviteModalOpen && (
        <Modal maxWidth="md" closeOnBackdrop={false} className="rounded-lg shadow-xl" hasUnsavedChanges={isInviteDirty}>
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold">Invite a Reviewer</h2>
            <button onClick={handleInviteCloseClick} className="text-muted-foreground/70 hover:text-foreground">
              <X size={20} />
            </button>
          </div>
          <div className="p-6">
            <p className="text-xs text-muted-foreground mb-4">
              Generates a one-time link an influencer or gifted-product reviewer can use to submit a review directly, no
              purchase or account required. It still goes through the same pending approval queue before it goes live.
            </p>

            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Product</label>
              {inviteProduct ? (
                <div className="flex items-center justify-between px-3 py-2 border rounded-lg bg-muted/50">
                  <span className="text-sm">{inviteProduct.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setInviteProduct(null);
                      setInviteQuery('');
                      loadDefaultInviteProducts();
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={inviteQuery}
                    onChange={(e) => handleInviteQueryChange(e.target.value)}
                    placeholder="Search products by name..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {inviteResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-card border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {inviteResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setInviteProduct(p);
                            setInviteResults([]);
                            setIsInviteDirty(true);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50"
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Email (optional)</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setIsInviteDirty(true);
                }}
                placeholder="influencer@example.com"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-muted-foreground/70 mt-1">
                If given, we'll email the link directly using the Review Invite template. Leave blank to just get a link
                to send yourself (e.g. via DM) — the reviewer will still need to enter their own email before they can
                submit, so there are no anonymous reviews either way.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground/80 mb-1">Name (optional)</label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => {
                  setInviteName(e.target.value);
                  setIsInviteDirty(true);
                }}
                placeholder="Jane Doe"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {inviteLink ? (
              <div className="mb-4">
                {inviteEmailSent && (
                  <p className="mb-2 text-xs font-medium text-green-700 dark:text-green-300 flex items-center gap-1">
                    <Check size={14} /> Emailed to {inviteEmail.trim()}
                  </p>
                )}
                <label className="block text-sm font-medium text-foreground/80 mb-1">Invite Link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteLink}
                    className="flex-1 px-3 py-2 border rounded-lg text-xs bg-muted/50 text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => copyLink(inviteLink)}
                    className="p-2 border rounded-lg hover:bg-muted/50"
                    title="Copy link"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {inviteEmailSent
                    ? 'Also copyable here as a backup.'
                    : 'Send this link to the reviewer directly (email, DM, etc).'}
                </p>
              </div>
            ) : (
              <button
                onClick={handleCreateInvite}
                disabled={inviteSubmitting || !inviteProduct}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {inviteSubmitting ? 'Generating...' : 'Generate Invite Link'}
              </button>
            )}
          </div>
        </Modal>
      )}

      {confirmBulkDelete && (
        <BulkConfirmModal
          title="Delete reviews"
          message="They move to the Deleted tab (kept for training reference) and disappear from the site."
          count={selection.count}
          confirmLabel="Delete"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => handleBulkAction('delete', 'deleted')}
          onClose={() => setConfirmBulkDelete(false)}
        />
      )}
    </div>
  );
};

export default ReviewsPage;
